import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import IngestionAlertService, {
  IngestionAlertRecipient,
  isIngestionAlertRequestError,
} from '../../services/IngestionAlertService';

const IngestionAlertRecipients: FC = () => {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState<IngestionAlertRecipient[]>([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    IngestionAlertService.list()
      .then(setRecipients)
      .catch(() => setError(t('ingestion_alerts_load_failed')))
      .finally(() => setLoading(false));
  }, [t]);

  const addRecipient = async () => {
    if (!email.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const recipient = await IngestionAlertService.create(email.trim());
      setRecipients((current) =>
        [...current, recipient].sort((a, b) => a.email.localeCompare(b.email))
      );
      setEmail('');
    } catch (requestError) {
      setError(
        isIngestionAlertRequestError(requestError, 409)
          ? t('ingestion_alerts_duplicate')
          : isIngestionAlertRequestError(requestError, 400)
          ? t('ingestion_alerts_invalid')
          : t('ingestion_alerts_load_failed')
      );
    } finally {
      setSaving(false);
    }
  };

  const updateActive = async (recipient: IngestionAlertRecipient, active: boolean) => {
    setBusyIds((current) => [...current, recipient.id]);
    setError('');
    try {
      const updated = await IngestionAlertService.setActive(recipient.id, active);
      setRecipients((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch {
      setError(t('ingestion_alerts_load_failed'));
    } finally {
      setBusyIds((current) => current.filter((id) => id !== recipient.id));
    }
  };

  const deleteRecipient = async (recipient: IngestionAlertRecipient) => {
    setBusyIds((current) => [...current, recipient.id]);
    setError('');
    try {
      await IngestionAlertService.remove(recipient.id);
      setRecipients((current) => current.filter((item) => item.id !== recipient.id));
    } catch {
      setError(t('ingestion_alerts_load_failed'));
    } finally {
      setBusyIds((current) => current.filter((id) => id !== recipient.id));
    }
  };

  return (
    <Box sx={{ maxWidth: 450, mt: 4 }}>
      <Typography variant="h5">{t('ingestion_alerts_section')}</Typography>
      <Divider />
      <Typography
        variant="body2"
        sx={{ mt: 2 }}
      >
        {t('ingestion_alerts_hint')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t('ingestion_alerts_email')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && addRecipient()}
        />
        <Button
          variant="contained"
          disabled={!email.trim() || saving}
          onClick={addRecipient}
        >
          {saving ? <CircularProgress size={20} /> : t('ingestion_alerts_add')}
        </Button>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
      {loading ? (
        <CircularProgress sx={{ mt: 2 }} />
      ) : recipients.length === 0 ? (
        <Typography sx={{ mt: 2 }}>{t('ingestion_alerts_empty')}</Typography>
      ) : (
        <Box sx={{ mt: 2 }}>
          {recipients.map((recipient) => {
            const busy = busyIds.includes(recipient.id);
            return (
              <Box
                key={recipient.id}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box>
                  <Typography>{recipient.email}</Typography>
                  {!recipient.active && (
                    <Typography variant="body2">{t('ingestion_alerts_paused')}</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={recipient.active}
                        disabled={busy}
                        onChange={(event) => updateActive(recipient, event.target.checked)}
                      />
                    }
                    label={t('ingestion_alerts_active')}
                  />
                  <IconButton
                    aria-label={t('ingestion_alerts_remove')}
                    disabled={busy}
                    onClick={() => deleteRecipient(recipient)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default IngestionAlertRecipients;
